import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals `resendInvitation` answers with.
 *
 * **Attention, the wording is ours, and this is not a preference.** The API's sentences are
 * written for whoever reads a log: two of the five are in French, three in English,
 * one of them says « MACHINE A already accepted ». They are notes for us, not
 * messages for a French operator: the same defect that shipped on the service sheet,
 * where an inventory note reached a client verbatim. What each code contributes is
 * **the gesture it calls for**, which is the only reason the API carries five of them
 * rather than one `FORBIDDEN`.
 *
 * Two of the five are also pre-empted by `resendBlocker`, which disables the button
 * with its reason before any call. They are still read here: the state can change
 * between the page load and the click, and a refusal that arrives anyway must not
 * fall through to « code inconnu ».
 */
export const resendRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'INVITATION_NOT_PENDING':
            return {
                title: c('Resend refusal').t`This person has already activated their account.`,
                guidance: c('Resend refusal')
                    .t`They activated it since this page was opened, so there is nothing left to send: they sign in with their password. Reload the sheet to see their current state.`,
            };
        case 'INVITATION_NOT_FOUND':
            return {
                title: c('Resend refusal').t`This person holds no pending access.`,
                guidance: c('Resend refusal')
                    .t`Every one of their memberships has been closed, so an invitation would lead to nothing. Reopen a membership first, then send the invitation again.`,
            };
        case 'ACCOUNT_DISABLED':
            return {
                title: c('Resend refusal').t`This account is disabled.`,
                guidance: c('Resend refusal')
                    .t`A disabled person cannot activate anything, so a new link would be dead on arrival. Reactivating the account is a separate gesture, and it comes first.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Resend refusal').t`This person no longer exists.`,
                guidance: c('Resend refusal')
                    .t`They carry no membership at all any more — deleted or fully detached since this page was opened. Go back to the list: the sheet you are reading is stale.`,
            };
        case 'MISSING_PERMISSION':
            // Attention, rendered after the click, and it cannot be otherwise: `viewer`
            // exposes the role but not the permissions behind it, so no screen can
            // know beforehand whether the actor holds `manage_users`.
            return {
                title: c('Resend refusal').t`You are not allowed to administer this person.`,
                guidance: c('Resend refusal')
                    .t`Sending an invitation requires the right to manage the users of one of their accounts, which your role does not carry. Ask for that right, or hand the gesture to whoever administers the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Resend refusal').t`This person is outside your portfolio.`,
                guidance: c('Resend refusal')
                    .t`They are attached only to accounts that are not entrusted to you. Ask for one of those accounts to be added to your portfolio, or hand the gesture over.`,
            };
        default:
            return {
                title: c('Resend refusal').t`The invitation was not sent.`,
                guidance: c('Resend refusal')
                    .t`This refusal is not one the screen knows how to read. Nothing has changed: the previous link, if there was one, still works exactly as before. The server's answer is below — it is what the support desk needs.`,
                // Kept only when we could not read the refusal. An unknown code with
                // no detail leaves the operator with nothing to report.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
